/**
 * Live Create Batch progress (BLU-1330 PR 2b).
 *
 * createBatch stays one synchronous request, and a 10,000-garment batch can
 * take tens of seconds. The backend keeps an in-memory entry per run and
 * `productionOrders.getBatchCreationStatus` reads it with no DB calls. This
 * hook polls that read every 2 s while there is something to watch:
 *
 *   - this tab's createBatch is in flight (`isCreating`);
 *   - this tab lost the createBatch response and is checking the outcome;
 *   - the location is busy with a run this tab did not start: one of mine
 *     (a reload mid-batch, a second tab, or a run this tab gave up checking)
 *     or a colleague's. The server takes one Create Batch per org per
 *     location at a time, so either blocks Create Batch here, and the poll is
 *     what unlocks it. Another org's run is never visible and never blocks.
 *
 * It also fetches once on mount, which is what makes a reload mid-batch
 * show the ticker again. Nothing else polls.
 *
 * WHICH ENTRY IS THIS TAB'S RUN. Every press sends a fresh `clientRunId`
 * (`beginRun`), and the server echoes it on the caller's own entries. A tab
 * remembers every id it sent, so its own earlier runs are never mistaken for
 * someone else's, and a run is only ever matched by its own id.
 *
 * WHO REPORTS AN OUTCOME (one toast per run, never two):
 *   - createBatch answered (success or a real error): the caller, as before.
 *     This hook stays silent about that run, even when a later poll shows it
 *     done.
 *   - createBatch's answer was lost (`beginChecking`): this hook. `done` calls
 *     the caller's own success path; `failed` gets a sticky error; losing sight
 *     of the run for 10 s gets a sticky "Couldn't confirm", and the tab
 *     forgets the run, so if it shows up still running it is treated as below.
 *   - a run of mine this tab did not start (seen running after a reload):
 *     this hook, once, when it finishes. A colleague's run gets no toast; the
 *     busy line just goes away.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import type { BatchCreationStatus, BatchCreationStatusEntry } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { parseBatchTooLargeError, parseOrderTagsConflictError } from '@/lib/trpc-error-utils';
import { SKIP_GLOBAL_ERROR_REPORT_META } from '@/lib/query-error-meta';
import { invalidateAfterBatchCreated } from './useBatches';

export const BATCH_CREATION_POLL_MS = 2_000;
/** Checking gives up after this long without seeing the run alive. */
export const BATCH_CREATION_UNCONFIRMED_MS = 10_000;

/**
 * One sticky slot for a create's outcome: a newer outcome replaces an older
 * one, and a success clears it.
 */
export const BATCH_CREATION_OUTCOME_TOAST_ID = 'batch-creation-outcome';

export type BatchCreationViewKind = 'creating' | 'checking' | 'resumed' | 'busy';

export interface BatchCreationView {
  kind: BatchCreationViewKind;
  /** null until the first poll that carries the run; always null for 'busy'. */
  entry: BatchCreationStatusEntry | null;
}

export function isBatchCreationFinished(entry: BatchCreationStatusEntry): boolean {
  return entry.phase === 'done' || entry.phase === 'failed';
}

function newest(entries: BatchCreationStatusEntry[]): BatchCreationStatusEntry | null {
  let best: BatchCreationStatusEntry | null = null;
  for (const entry of entries) {
    if (!best || Date.parse(entry.startedAt) > Date.parse(best.startedAt)) best = entry;
  }
  return best;
}

/** The entry this tab sent `clientRunId` for, if the server has it. */
function runEntry(entries: BatchCreationStatusEntry[], clientRunId: string | null): BatchCreationStatusEntry | null {
  if (clientRunId === null) return null;
  return entries.find((entry) => entry.clientRunId === clientRunId) ?? null;
}

function isOwnRun(entry: BatchCreationStatusEntry, ownRunIds: ReadonlySet<string>): boolean {
  return entry.clientRunId != null && ownRunIds.has(entry.clientRunId);
}

interface RunsElsewhere {
  /** My newest unfinished run this tab did not start (another tab, or before a reload). */
  resumed: BatchCreationStatusEntry | null;
  /** A colleague's unfinished run holds the location. */
  busy: boolean;
}

/** What the status says about unfinished runs this tab did not start. */
function runsElsewhere(status: BatchCreationStatus | undefined, ownRunIds: ReadonlySet<string>): RunsElsewhere {
  const unfinished = (status?.entries ?? []).filter((entry) => !isBatchCreationFinished(entry));
  const notOwn = unfinished.filter((entry) => !isOwnRun(entry, ownRunIds));
  return {
    resumed: newest(notOwn.filter((entry) => entry.isMine)),
    busy: notOwn.some((entry) => !entry.isMine),
  };
}

/**
 * The garment-ceiling refusal: the operator's numbers and the way out, sticky
 * until dismissed, kept out of Sentry. Shared by createBatch's own catch and by
 * a failed run found while checking.
 */
export function toastBatchTooLarge(t: TFunction<'production'>, error: unknown): boolean {
  const tooLarge = parseBatchTooLargeError(error);
  if (!tooLarge) return false;
  toast.error(t('batch.tooLarge', { count: tooLarge.count, max: tooLarge.max }), {
    id: BATCH_CREATION_OUTCOME_TOAST_ID,
    duration: Infinity,
  });
  return true;
}

/**
 * The DTF and DTG order tag refusal: orders carrying a tag of each list, so
 * nothing was batched. Names the orders (and how many more) so the operator can
 * fix their tags or leave them out; sticky until dismissed. Shared by
 * createBatch's own catch and by a failed run found while checking.
 */
export function toastOrderTagsConflict(t: TFunction<'production'>, error: unknown): boolean {
  const conflict = parseOrderTagsConflictError(error);
  if (!conflict) return false;
  const named = conflict.names.join(', ');
  const more = conflict.total - conflict.names.length;
  const orders = more > 0 ? t('batch.progress.orderTagsConflictMore', { orders: named, more }) : named;
  toast.error(t('batch.progress.orderTagsConflict', { orders, count: conflict.total }), {
    id: BATCH_CREATION_OUTCOME_TOAST_ID,
    duration: Infinity,
  });
  return true;
}

interface TabRuns {
  /** The id sent with this tab's latest createBatch. */
  latest: string | null;
  /** Every id this tab has sent. */
  own: ReadonlySet<string>;
}

const NO_RUNS: TabRuns = { latest: null, own: new Set() };

export interface UseBatchCreationStatusOptions {
  /** createBatch's `isPending`. */
  isCreating: boolean;
}

export interface UseBatchCreationStatusResult {
  /** What the progress pill shows; null = no pill. */
  view: BatchCreationView | null;
  /**
   * True while this tab's run is in flight or being checked, or while another
   * run of this org holds the location: no new Create Batch.
   */
  blocksCreate: boolean;
  /** Call right before createBatch, with the `clientRunId` it sends. */
  beginRun: (clientRunId: string) => void;
  /**
   * createBatch failed without the handler's verdict (network, socket, proxy).
   * Keep polling and report the real outcome of run `clientRunId`; `onCreated`
   * is the caller's normal success path (clear selection, notify, toast).
   */
  beginChecking: (clientRunId: string, onCreated: (batchNumber: number) => void) => void;
  /**
   * createBatch was refused because another run of this org holds the
   * location. Look now; the poll then keeps going until the location is free.
   */
  watchLocation: () => void;
}

export function useBatchCreationStatus(
  locationId: string | undefined,
  { isCreating }: UseBatchCreationStatusOptions
): UseBatchCreationStatusResult {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();

  const [runs, setRuns] = useState<TabRuns>(NO_RUNS);
  const [checking, setChecking] = useState<{ clientRunId: string; since: number } | null>(null);
  const onCreatedRef = useRef<((batchNumber: number) => void) | null>(null);

  const status = trpc.productionOrders.getBatchCreationStatus.useQuery(
    { locationId: locationId ?? '' },
    {
      enabled: !!locationId,
      // The next 2 s poll is the retry; a retry storm through the China proxy
      // would only delay it.
      retry: false,
      // ...and a failed poll on a flaky link is a breadcrumb, not a Sentry
      // error every 2 s.
      meta: SKIP_GLOBAL_ERROR_REPORT_META,
      // A coordinator who tabs away mid-batch still gets a true answer when
      // they come back, and the 10 s checking deadline stays honest.
      refetchIntervalInBackground: true,
      refetchInterval: (query) => {
        if (isCreating || checking !== null) return BATCH_CREATION_POLL_MS;
        const elsewhere = runsElsewhere(query.state.data, runs.own);
        return elsewhere.resumed !== null || elsewhere.busy ? BATCH_CREATION_POLL_MS : false;
      },
    }
  );
  const { refetch, dataUpdatedAt } = status;
  const entries = useMemo(() => status.data?.entries ?? [], [status.data]);
  const elsewhere = useMemo(() => runsElsewhere(status.data, runs.own), [status.data, runs.own]);

  const latestEntry = useMemo(() => runEntry(entries, runs.latest), [entries, runs.latest]);
  const checkingEntry = useMemo(
    () => runEntry(entries, checking?.clientRunId ?? null),
    [entries, checking]
  );

  const view = useMemo<BatchCreationView | null>(() => {
    if (isCreating) return { kind: 'creating', entry: latestEntry };
    if (checking !== null) {
      return {
        kind: 'checking',
        entry: checkingEntry && !isBatchCreationFinished(checkingEntry) ? checkingEntry : null,
      };
    }
    if (elsewhere.resumed) return { kind: 'resumed', entry: elsewhere.resumed };
    if (elsewhere.busy) return { kind: 'busy', entry: null };
    return null;
  }, [isCreating, checking, latestEntry, checkingEntry, elsewhere]);

  const blocksCreate = isCreating || checking !== null || elsewhere.resumed !== null || elsewhere.busy;

  const beginRun = useCallback((clientRunId: string) => {
    setRuns((prev) => ({ latest: clientRunId, own: new Set(prev.own).add(clientRunId) }));
  }, []);

  const beginChecking = useCallback(
    (clientRunId: string, onCreated: (batchNumber: number) => void) => {
      onCreatedRef.current = onCreated;
      setChecking({ clientRunId, since: Date.now() });
      // The last poll can be up to 2 s old; ask now.
      void refetch();
    },
    [refetch]
  );

  const watchLocation = useCallback(() => {
    void refetch();
  }, [refetch]);

  const toastFailure = useCallback(
    (entry: BatchCreationStatusEntry) => {
      if (toastBatchTooLarge(t, entry.errorMessage)) return;
      if (toastOrderTagsConflict(t, entry.errorMessage)) return;
      // The server's own text is English-only; the operator reads zh as often.
      toast.error(t('batch.progress.failed'), { id: BATCH_CREATION_OUTCOME_TOAST_ID, duration: Infinity });
    },
    [t]
  );

  // --- Checking: settle on the run's real outcome. -------------------------
  useEffect(() => {
    if (checking === null || !checkingEntry || !isBatchCreationFinished(checkingEntry)) return;
    setChecking(null);
    const onCreated = onCreatedRef.current;
    onCreatedRef.current = null;
    if (checkingEntry.phase === 'done' && checkingEntry.batchNumber !== null) {
      invalidateAfterBatchCreated(utils);
      toast.dismiss(BATCH_CREATION_OUTCOME_TOAST_ID);
      onCreated?.(checkingEntry.batchNumber);
    } else {
      toastFailure(checkingEntry);
    }
  }, [checking, checkingEntry, utils, toastFailure]);

  // --- Checking: give up after 10 s without seeing the run alive. ----------
  // Alive = the latest successful poll shows the run still running. A poll
  // that fails, or a run that vanished (backend restart, another replica),
  // does not move the deadline. Giving up also forgets the run: if it shows up
  // still running, it is a run of mine this tab did not start, so it blocks
  // Create Batch, shows the ticker, and is reported once when it finishes.
  const checkingSince = checking?.since ?? null;
  const checkingRunId = checking?.clientRunId ?? null;
  const runAlive = checkingEntry !== null && !isBatchCreationFinished(checkingEntry);
  const aliveAt = checkingSince === null ? 0 : Math.max(checkingSince, runAlive ? dataUpdatedAt : 0);
  useEffect(() => {
    if (checkingSince === null || checkingRunId === null) return;
    const wait = Math.max(0, aliveAt + BATCH_CREATION_UNCONFIRMED_MS - Date.now());
    const timer = setTimeout(() => {
      setChecking(null);
      setRuns((prev) => {
        const own = new Set(prev.own);
        own.delete(checkingRunId);
        return { ...prev, own };
      });
      onCreatedRef.current = null;
      toast.error(t('batch.progress.unconfirmed'), {
        id: BATCH_CREATION_OUTCOME_TOAST_ID,
        duration: Infinity,
        action: {
          label: t('batch.progress.refreshQueue'),
          onClick: () => invalidateAfterBatchCreated(utils),
        },
      });
    }, wait);
    return () => clearTimeout(timer);
  }, [checkingSince, checkingRunId, aliveAt, t, utils]);

  // --- A run of mine this tab did not start: report it once when it finishes.
  const watchedResumedRef = useRef(new Set<string>());
  useEffect(() => {
    const watched = watchedResumedRef.current;
    for (const entry of entries) {
      if (!entry.isMine || isOwnRun(entry, runs.own)) continue;
      if (!isBatchCreationFinished(entry)) {
        watched.add(entry.entryId);
        continue;
      }
      if (!watched.delete(entry.entryId)) continue;
      if (entry.phase === 'done' && entry.batchNumber !== null) {
        invalidateAfterBatchCreated(utils);
        toast.dismiss(BATCH_CREATION_OUTCOME_TOAST_ID);
        toast.success(t('batch.progress.createdResumed', { number: entry.batchNumber }));
      } else if (entry.phase === 'failed') {
        toastFailure(entry);
      }
    }
  }, [entries, runs.own, utils, t, toastFailure]);

  return { view, blocksCreate, beginRun, beginChecking, watchLocation };
}
